import { Alert, Button, Input } from "antd"
import React, { useState } from "react"
import { Link } from "react-router-dom"
import styled from "styled-components"

import { postPasswordSignin } from "../../actions/passwordAuth"
import {
  buildPostLoginLocation,
  isValidEmail,
  signinErrorKey,
} from "../../helpers/passwordAuth"
import { t } from "../../lib/i18n"
import routes from "../../router/routes"

import ConfirmationResendForm from "./ConfirmationResendForm"
import { lightAntOverrides } from "./lightAntOverrides"

const PasswordLoginForm = (): JSX.Element => {
  const [email, setEmail] = useState("")
  const [password, setPassword] = useState("")
  const [submitting, setSubmitting] = useState(false)
  const [errorKey, setErrorKey] = useState<string | null>(null)
  const [needsConfirmation, setNeedsConfirmation] = useState(false)

  const onSubmit = async (event: React.FormEvent) => {
    event.preventDefault()
    setNeedsConfirmation(false)
    if (!isValidEmail(email)) {
      setErrorKey("invalid_email")
      return
    }
    if (!password) {
      setErrorKey("password_required")
      return
    }

    setSubmitting(true)
    setErrorKey(null)
    const result = await postPasswordSignin({ email: email.trim(), password })

    if (result.ok) {
      window.location.href = buildPostLoginLocation(
        result.data.redirect_uri,
        window.location.search,
      )
      return
    }

    setSubmitting(false)
    setErrorKey(signinErrorKey(result))
    setNeedsConfirmation(result.status === 403)
  }

  return (
    <Root>
      <StyledForm onSubmit={onSubmit} noValidate>
        <Input
          type="email"
          aria-label={t("email")}
          placeholder={t("email")}
          value={email}
          onChange={(e) => setEmail(e.target.value)}
          autoComplete="username"
        />
        <Input.Password
          aria-label={t("password")}
          placeholder={t("password")}
          value={password}
          onChange={(e) => setPassword(e.target.value)}
          autoComplete="current-password"
        />
        {errorKey && <Alert type="error" message={t(errorKey)} />}
        <Button type="primary" htmlType="submit" loading={submitting} block>
          {t("password_login_submit")}
        </Button>
      </StyledForm>
      {needsConfirmation && <ConfirmationResendForm initialEmail={email} />}
      <Link to={routes.PasswordReset.path}>{t("forgot_password")}</Link>
    </Root>
  )
}

const Root = styled.div`
  ${lightAntOverrides}

  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 12px 0;
`

const StyledForm = styled.form`
  display: flex;
  flex-direction: column;
  gap: 12px 0;
  width: 360px;
  max-width: 100%;
`

export default PasswordLoginForm
